import { cilSave, cilTrash } from '@coreui/icons';

import CIcon from '@coreui/icons-react';

import { CButton, CFormInput, CTableDataCell, CTableRow } from '@coreui/react';

import React, { useState } from 'react';

import { brokenSubscriptionsApi } from '../api';

import type { ShopifyContractLine } from '../types';

import { QUANTITY_MIN } from './BrokenSubscriptionDetailPage.constants';
import { formatPrice, lineSubtotal } from './BrokenSubscriptionDetailPage.utils';

export type ShopifyLineRowProps = {
  line: ShopifyContractLine;
  canEdit: boolean;
  shopifyCustomerId: number;
  actionInProgress: boolean;
  onSave: () => void;
  onError: (msg: string) => void;
  onDelete: () => void;
};

export function ShopifyLineRow({
  line,
  canEdit,
  shopifyCustomerId,
  actionInProgress,
  onSave,
  onError,
  onDelete,
}: ShopifyLineRowProps) {
  const displayQty = line.quantity != null ? Number(line.quantity) : '';
  const [qty, setQty] = useState(String(displayQty !== '' ? displayQty : (line.quantity ?? '')));
  const [previousQuantity, setPreviousQuantity] = useState(line.quantity);
  const [saving, setSaving] = useState(false);

  if (line.quantity !== previousQuantity) {
    setPreviousQuantity(line.quantity);
    const next = line.quantity != null ? Number(line.quantity) : '';
    setQty(String(next !== '' ? next : (line.quantity ?? '')));
  }

  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQty(e.target.value);
  };

  const handleSave = async () => {
    const num = parseInt(qty, 10);
    if (Number.isNaN(num) || num < QUANTITY_MIN) {
      onError(`Quantity must be at least ${QUANTITY_MIN}`);
      return;
    }
    setSaving(true);
    try {
      await brokenSubscriptionsApi.updateShopifyLineQty(
        shopifyCustomerId,
        String(line.lineId),
        num,
      );
      onSave();
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String(e.message) : 'Save failed';
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CTableRow>
      <CTableDataCell>{line.productName ?? ''}</CTableDataCell>
      <CTableDataCell>{line.productVariantId ?? ''}</CTableDataCell>
      <CTableDataCell>{line.productSku ?? ''}</CTableDataCell>
      <CTableDataCell>{String(line.lineId)}</CTableDataCell>
      <CTableDataCell>
        {canEdit ? (
          <CFormInput
            type="number"
            min={QUANTITY_MIN}
            value={qty}
            onChange={handleQtyChange}
            disabled={actionInProgress}
            style={{ maxWidth: 80 }}
          />
        ) : line.quantity != null ? (
          String(line.quantity)
        ) : (
          ''
        )}
      </CTableDataCell>
      <CTableDataCell>{formatPrice(line.linePrice)}</CTableDataCell>
      <CTableDataCell>
        {(() => {
          const st = lineSubtotal(line);
          return st != null ? formatPrice(st) : '';
        })()}
      </CTableDataCell>
      {canEdit && (
        <CTableDataCell>
          <CButton
            color="primary"
            size="sm"
            variant="ghost"
            aria-label="Save quantity"
            title="Save quantity"
            onClick={(...args: Parameters<typeof handleSave>) => void handleSave(...args)}
            disabled={saving || actionInProgress}
            className="me-1"
          >
            <CIcon icon={cilSave} />
          </CButton>
          <CButton
            color="danger"
            size="sm"
            variant="ghost"
            aria-label="Delete line"
            title="Delete line"
            onClick={onDelete}
            disabled={actionInProgress}
          >
            <CIcon icon={cilTrash} />
          </CButton>
        </CTableDataCell>
      )}
    </CTableRow>
  );
}
